import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Play, X } from "lucide-react";
import { ScrollReveal, SectionLabel, EASE } from "./Reveal";

const services = [
  {
    title: "Cinematic Films",
    desc: "High-end brand films, promotional videos and storytelling content.",
    price: "From £350",
  },
  {
    title: "Social Content",
    desc: "Short-form videos designed for Instagram, TikTok and YouTube.",
    price: "From £150",
  },
  {
    title: "Event Films",
    desc: "Cinematic video and photography that captures the atmosphere and important moments.",
    price: "From £250",
  },
];

const weddingFrames = [
  "/images/wedding-couple-1.webp",
  "/videos/wedding-1-poster.jpg",
  "/images/img-0373.jpg",
  "/images/img-0372.jpg",
  "/videos/wedding-2-poster.jpg",
];

export const ServicesSection = () => {
  const [frame, setFrame] = useState(null);
  const [playTrailer, setPlayTrailer] = useState(false);
  const touchX = useRef(null);
  const trailerRef = useRef(null);
  const trailerInView = useInView(trailerRef, { amount: 0.4 });

  useEffect(() => {
    const v = trailerRef.current;
    if (!v) return;
    if (trailerInView) v.play().catch(() => {});
    else v.pause();
  }, [trailerInView]);

  const stepFrame = useCallback((dir) => {
    setFrame((cur) => (cur === null ? cur : (cur + dir + weddingFrames.length) % weddingFrames.length));
  }, []);

  useEffect(() => {
    if (frame === null) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") stepFrame(1);
      if (e.key === "ArrowLeft") stepFrame(-1);
      if (e.key === "Escape") setFrame(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [frame, stepFrame]);

  return (
  <section id="services" data-testid="services-section" className="py-24 lg:py-32">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <ScrollReveal>
        <SectionLabel index="02" title="Services" />
        <h2 className="font-display leading-[0.95] text-5xl sm:text-6xl lg:text-7xl max-w-4xl">
          Content built to remember.
        </h2>
        <p className="mt-6 text-base md:text-lg text-[#8A8A85] max-w-xl">
          Content built to make people stop, watch and remember.
        </p>
      </ScrollReveal>

      <div className="mt-16 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {services.map((s, i) => (
          <ScrollReveal key={s.title} delay={i * 0.08} className="h-full">
            <div
              data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group bg-[#0A0A0A] hover:bg-[#111111] p-10 lg:p-12 h-full flex flex-col transition-colors duration-500"
            >
              <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#55554F]">0{i + 1}</span>
              <h3 className="font-display text-2xl lg:text-3xl mt-6">{s.title}</h3>
              <p className="text-sm text-[#8A8A85] leading-relaxed mt-4 flex-1">{s.desc}</p>
              <p className="font-display text-lg mt-8 text-[#F2F0EB]">{s.price}</p>
            </div>
          </ScrollReveal>
        ))}
      </div>

      <ScrollReveal delay={0.15}>
        <div className="mt-16 flex flex-col sm:flex-row sm:items-center justify-between gap-6 border border-white/10 p-8 lg:p-10">
          <div>
            <h3 className="font-display text-xl lg:text-2xl">Not sure what you need?</h3>
            <p className="text-sm text-[#8A8A85] mt-2 max-w-md">
              Let's have a conversation about your goals and create a package that works for you.
            </p>
          </div>
          <button
            data-testid="services-get-in-touch-btn"
            onClick={() => window.__openEnquiry()}
            className="group flex items-center gap-3 bg-[#F2F0EB] text-[#0A0A0A] font-display text-sm tracking-[0.08em] px-8 py-4 rounded-full w-fit hover:scale-105 transition-transform duration-300"
          >
            Get in Touch
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-300" />
          </button>
        </div>
      </ScrollReveal>

      <ScrollReveal delay={0.1}>
        <div className="mt-20 border-t border-white/10 pt-14" data-testid="wedding-pricing-block">
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-12" data-testid="wedding-gallery-strip">
            {weddingFrames.map((src, i) => (
              <button
                key={src}
                data-testid={`wedding-frame-${i + 1}`}
                onClick={() => setFrame(i)}
                className="group aspect-[3/4] overflow-hidden border border-white/10 cursor-pointer"
              >
                <img
                  src={src}
                  alt="Etia Media wedding frame"
                  loading="lazy"
                  className="w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-[transform,filter] duration-700"
                />
              </button>
            ))}
          </div>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#55554F] mb-3">Weddings</p>
          <h3 className="font-display text-3xl lg:text-4xl">Your big day, filmed like a film.</h3>
          <p className="mt-3 text-sm text-[#8A8A85] max-w-xl leading-relaxed">
            No two weddings cost the same — it depends on where, how long, and whether
            you need a solo filmmaker or a full team. Tell me your budget and what
            you're dreaming of, and I'll build a package around it.
          </p>
          <div
            data-testid="wedding-trailer-player"
            onClick={() => setPlayTrailer(true)}
            className="group relative mt-10 aspect-video overflow-hidden border border-white/10 cursor-pointer"
          >
            <video
              ref={trailerRef}
              src="/videos/wedding-1.mp4"
              poster="/videos/wedding-1-poster.jpg"
              muted
              loop
              playsInline
              preload="metadata"
              className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-[opacity,transform] duration-700"
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              <span className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-[#F2F0EB] text-[#0A0A0A] flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                <Play className="w-6 h-6 fill-current ml-1" />
              </span>
              <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#F2F0EB]">
                Watch a wedding trailer
              </span>
            </div>
            <span
              data-testid="wedding-trailer-sound-badge"
              className="absolute bottom-4 left-4 flex items-center gap-2 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/20 px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#F2F0EB]"
            >
              <Play className="w-3 h-3 fill-current" />
              Tap for sound
            </span>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            {["Location", "Duration", "Solo filmmaker or full team", "Films & deliverables"].map((f) => (
              <span
                key={f}
                data-testid={`wedding-factor-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A85] border border-white/15 px-4 py-2.5"
              >
                {f}
              </span>
            ))}
          </div>
          <button
            data-testid="wedding-enquire-btn"
            onClick={() => window.__openEnquiry("Wedding")}
            className="group mt-8 flex items-center gap-3 font-display text-sm tracking-[0.08em] border-b border-[#F2F0EB] pb-2 hover:gap-5 transition-all duration-300"
          >
            Tell Me Your Budget
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </ScrollReveal>
    </div>

    <AnimatePresence>
      {playTrailer && (
        <motion.div
          data-testid="wedding-trailer-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="fixed inset-0 z-[90] bg-[#0A0A0A]/95 backdrop-blur-xl flex flex-col items-center justify-center p-6"
          onClick={() => setPlayTrailer(false)}
        >
          <button
            data-testid="wedding-trailer-close"
            className="absolute top-6 right-6 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-[#F2F0EB] hover:bg-[#F2F0EB] hover:text-[#0A0A0A] transition-colors duration-300 z-10"
          >
            <X className="w-5 h-5" />
          </button>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.35em] text-[#8A8A85] mb-6">
            Weddings — A Trailer by Etia Media
          </p>
          <motion.div
            initial={{ scale: 0.94, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.94, y: 20 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              data-testid="wedding-trailer-video"
              src="/videos/wedding-1.mp4"
              poster="/videos/wedding-1-poster.jpg"
              controls
              autoPlay
              playsInline
              className="w-full aspect-video border border-white/10 bg-black"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>

    <AnimatePresence>
      {frame !== null && (
        <motion.div
          data-testid="wedding-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="fixed inset-0 z-[90] bg-[#0A0A0A]/95 backdrop-blur-xl flex items-center justify-center p-6"
          onClick={() => setFrame(null)}
          onTouchStart={(e) => { touchX.current = e.touches?.[0]?.clientX ?? null; }}
          onTouchEnd={(e) => {
            const x = e.changedTouches?.[0]?.clientX;
            if (touchX.current === null || x === undefined) { touchX.current = null; return; }
            const dx = x - touchX.current;
            if (Math.abs(dx) > 50) stepFrame(dx < 0 ? 1 : -1);
            touchX.current = null;
          }}
        >
          <button
            data-testid="wedding-lightbox-close"
            className="absolute top-6 right-6 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-[#F2F0EB] hover:bg-[#F2F0EB] hover:text-[#0A0A0A] transition-colors duration-300 z-10"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            data-testid="wedding-lightbox-prev"
            onClick={(e) => { e.stopPropagation(); stepFrame(-1); }}
            className="absolute left-4 lg:left-10 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-[#F2F0EB] hover:bg-[#F2F0EB] hover:text-[#0A0A0A] transition-colors duration-300 z-10"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            data-testid="wedding-lightbox-next"
            onClick={(e) => { e.stopPropagation(); stepFrame(1); }}
            className="absolute right-4 lg:right-10 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-[#F2F0EB] hover:bg-[#F2F0EB] hover:text-[#0A0A0A] transition-colors duration-300 z-10"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <motion.div
            key={frame}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="max-h-[85vh] max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              data-testid="wedding-lightbox-img"
              src={weddingFrames[frame]}
              alt="Etia Media wedding frame"
              className="max-h-[80vh] w-auto max-w-full object-contain border border-white/10"
            />
            <p className="mt-4 font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#55554F] text-center">
              {frame + 1} / {weddingFrames.length} · ← → or swipe · Esc to close
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  </section>
  );
};
